import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Handshake, ArrowRight, CheckCircle, Building2, ShoppingBag, 
  Database, Users, TrendingUp, Shield, DollarSign, Zap,
  BarChart3, Globe, Award, Target, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PartnersPage = () => {
  // Partner types
  const partnerTypes = [
    {
      icon: Building2,
      title: "ERP & Software Vendors",
      description: "Integrate PriceStrike into your platform to give your SMB customers enterprise-grade pricing intelligence.",
      benefits: [
        "White-label integration options",
        "API-first architecture",
        "Revenue share programs",
        "Co-marketing opportunities"
      ],
      fit: "You sell business software to SMBs and want to add pricing optimization to your value stack."
    },
    {
      icon: ShoppingBag,
      title: "E-commerce & Shopify Agencies",
      description: "Help your retail clients maximize margins with data-driven pricing strategies that actually work.",
      benefits: [
        "Implementation partnerships",
        "Recurring revenue streams",
        "Client retention tool",
        "Differentiated service offering"
      ],
      fit: "You build and manage online stores for SMB retailers who struggle with pricing."
    },
    {
      icon: Users,
      title: "Business Consultants",
      description: "Add pricing optimization to your consulting toolkit with a platform that delivers measurable results.",
      benefits: [
        "Certified partner program",
        "Training and enablement",
        "Lead referral program",
        "Client success resources"
      ],
      fit: "You advise SMBs on operations, finance, or growth and want to expand your service offerings."
    },
    {
      icon: Database,
      title: "Data & Analytics Providers",
      description: "Your market data becomes more valuable when paired with actionable pricing recommendations.",
      benefits: [
        "Data integration partnerships",
        "Enhanced data monetization",
        "Joint solution development",
        "Technical collaboration"
      ],
      fit: "You provide market data, competitive intelligence, or analytics to SMB verticals."
    }
  ];

  // Partner use cases - SMB focused, credible
  const useCases = [
    {
      partner: "Regional Shopify Agency",
      partnerType: "E-commerce Agency",
      client: "Multi-location pet supply retailer",
      challenge: "Client had 1,200 SKUs across 3 stores and Shopify, with pricing managed in spreadsheets. Agency was doing monthly site updates but couldn't help with the pricing chaos.",
      solution: "Agency implemented PriceStrike as part of a store optimization project. Setup took 2 weeks including data migration and staff training.",
      results: [
        "23% margin improvement in first quarter",
        "Client renewed annual retainer at 40% higher rate",
        "Agency won 3 referrals from the success story"
      ],
      partnerBenefit: "Transformed a one-time project into ongoing managed services revenue.",
      quote: "PriceStrike let us offer something our competitors couldn't. Now pricing optimization is part of every proposal.",
      quotePerson: "Director of Client Services, Midwest e-commerce agency"
    },
    {
      partner: "ERP Implementation Firm",
      partnerType: "Software Consultant",
      client: "Building materials distributor with 180 B2B accounts",
      challenge: "Client was implementing new ERP but had no solution for the complex contractor pricing tiers that lived in the sales manager's head.",
      solution: "Partner integrated PriceStrike alongside ERP rollout, using it to systematize the pricing logic that was previously tribal knowledge.",
      results: [
        "Pricing admin time reduced by 75%",
        "New sales reps productive in 2 weeks vs 6 months",
        "Client avoided $40K custom development cost"
      ],
      partnerBenefit: "Expanded scope of engagement and created ongoing optimization revenue.",
      quote: "Every ERP project has a pricing problem hiding in it. PriceStrike gives us a packaged solution instead of custom builds.",
      quotePerson: "Partner, Regional ERP consulting firm"
    },
    {
      partner: "Fractional CFO Practice",
      partnerType: "Business Consultant",
      client: "Growing fitness studio chain (6 locations)",
      challenge: "CFO identified that membership pricing hadn't been reviewed in 3 years despite cost increases. Manual analysis would take weeks.",
      solution: "Used PriceStrike to model pricing scenarios and present board-ready recommendations in days instead of weeks.",
      results: [
        "Implemented tiered pricing that increased MRR by 32%",
        "Reduced member churn by aligning value with price points",
        "CFO engagement extended from 3 months to ongoing"
      ],
      partnerBenefit: "Delivered measurable ROI that justified continued engagement.",
      quote: "I can now show clients the revenue impact of pricing decisions with real data. It's changed how I deliver value.",
      quotePerson: "Fractional CFO serving fitness and wellness sector"
    }
  ];

  // Why partner with us
  const whyPartner = [
    {
      icon: Target,
      title: "Product Creates Pull",
      description: "SMBs are actively looking for pricing solutions. 73% of business owners say pricing is their #1 operational challenge."
    },
    {
      icon: Shield,
      title: "Low Delivery Risk",
      description: "14-day free trial, fast implementation, and proven ROI. Your clients see results before you invoice."
    },
    {
      icon: DollarSign,
      title: "Upside for You",
      description: "Revenue share, referral fees, or expanded service scope. Choose the model that fits your business."
    },
    {
      icon: Zap,
      title: "Fast Time to Value",
      description: "Most implementations complete in 2-4 weeks. No 6-month projects that strain client relationships."
    }
  ];

  // Partner program tiers
  const programTiers = [
    {
      name: "Referral Partner",
      description: "Recommend PriceStrike and earn on successful referrals",
      features: [
        "15% referral commission",
        "Partner portal access",
        "Marketing materials",
        "Dedicated partner manager"
      ]
    },
    {
      name: "Implementation Partner",
      description: "Deliver PriceStrike implementations for your clients",
      features: [
        "Implementation certification",
        "Priority support channel",
        "Co-branded collateral",
        "Lead sharing program"
      ]
    },
    {
      name: "Strategic Partner",
      description: "Deep integration and go-to-market alignment",
      features: [
        "API integration support",
        "Joint product roadmap input",
        "Revenue share arrangements",
        "Executive sponsorship"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <main className="pt-40 pb-24">
        {/* Hero Section */}
        <section className="px-6 mb-24">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
              <Handshake className="w-4 h-4 text-[#2563EB]" />
              <span className="text-[#2563EB] font-medium text-sm">Partner Program</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-[#F8FAFC] mb-6">
              Grow Your Business with
              <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> PriceStrike</span>
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto mb-8">
              Help SMB clients unlock pricing intelligence while creating new revenue streams for your practice. 
              We've built the partnerships program around what actually matters to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=partner">
                <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-lg">
                  Become a Partner
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Partner Video Section */}
        <section className="px-6 mb-24">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-[#2563EB]/30 shadow-2xl shadow-[#2563EB]/20">
              <video 
                controls
                className="w-full aspect-video bg-[#0A0F1C]"
                poster="/partner-poster.jpg"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/r4icpgme_Partner%20Man.mov" 
                  type="video/quicktime" 
                />
                <source 
                  src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/r4icpgme_Partner%20Man.mov" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-[#94A3B8] mt-4">See how partners are growing with PriceStrike</p>
          </div>
        </section>

        {/* Why Partner Section */}
        <section className="px-6 mb-24 bg-gradient-to-b from-[#111827]/50 to-transparent py-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Why Partners Choose PriceStrike
              </h2>
              <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
                We understand what matters to partners: client success, delivery confidence, and business upside.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyPartner.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6 hover:border-[#2563EB]/40 transition-colors">
                    <div className="w-12 h-12 bg-[#2563EB]/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">{item.title}</h3>
                    <p className="text-[#94A3B8] text-sm">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partner Types Section */}
        <section className="px-6 mb-24">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Where Do You Fit?
              </h2>
              <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
                PriceStrike works with partners across the SMB ecosystem. Find your path.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {partnerTypes.map((partner, index) => {
                const Icon = partner.icon;
                return (
                  <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 hover:border-[#2563EB]/40 transition-colors">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{partner.title}</h3>
                        <p className="text-[#94A3B8]">{partner.description}</p>
                      </div>
                    </div>
                    
                    <div className="bg-[#0A0F1C]/50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-[#2563EB] font-medium mb-1">Best Fit:</p>
                      <p className="text-[#94A3B8] text-sm">{partner.fit}</p>
                    </div>
                    
                    <div className="space-y-2">
                      {partner.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-[#F8FAFC] text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="px-6 mb-24 bg-gradient-to-b from-[#111827]/50 to-transparent py-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Partner Success Stories
              </h2>
              <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
                Real results from partners serving real SMB clients.
              </p>
            </div>
            
            <div className="space-y-8">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl overflow-hidden">
                  <div className="p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                      <span className="bg-[#2563EB]/10 border border-[#2563EB]/30 text-[#2563EB] text-sm font-medium px-3 py-1 rounded-full">
                        {useCase.partnerType}
                      </span>
                      <span className="text-[#94A3B8] text-sm">→</span>
                      <span className="text-[#F8FAFC] text-sm font-medium">{useCase.client}</span>
                    </div>
                    
                    <div className="grid lg:grid-cols-3 gap-8">
                      <div>
                        <h4 className="text-sm font-semibold text-[#2563EB] uppercase tracking-wide mb-2">Challenge</h4>
                        <p className="text-[#94A3B8]">{useCase.challenge}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-[#2563EB] uppercase tracking-wide mb-2">Solution</h4>
                        <p className="text-[#94A3B8]">{useCase.solution}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-[#2563EB] uppercase tracking-wide mb-2">Results</h4>
                        <ul className="space-y-2">
                          {useCase.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-[#F8FAFC] text-sm">{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-6 border-t border-[#2563EB]/20">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2 inline-flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 text-sm font-medium">Partner Benefit: {useCase.partnerBenefit}</span>
                        </div>
                      </div>
                      <blockquote className="mt-4 italic text-[#94A3B8]">
                        "{useCase.quote}"
                        <cite className="block mt-2 text-sm text-[#F8FAFC] not-italic">— {useCase.quotePerson}</cite>
                      </blockquote>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Program Tiers */}
        <section className="px-6 mb-24">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Partner Program Tiers
              </h2>
              <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
                Choose the engagement level that matches your business model.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {programTiers.map((tier, index) => (
                <div key={index} className={`bg-gradient-to-br from-[#111827] to-[#0A0F1C] border rounded-2xl p-8 ${index === 1 ? 'border-[#2563EB] ring-1 ring-[#2563EB]/50' : 'border-[#2563EB]/20'}`}>
                  {index === 1 && (
                    <div className="bg-[#2563EB] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{tier.name}</h3>
                  <p className="text-[#94A3B8] text-sm mb-6">{tier.description}</p>
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-[#F8FAFC] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-[#2563EB]/20 to-[#3B82F6]/20 border border-[#2563EB]/30 rounded-3xl p-12 text-center">
              <Handshake className="w-16 h-16 text-[#2563EB] mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Ready to Partner?
              </h2>
              <p className="text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
                Join the partners helping SMBs unlock better margins. 
                Let's explore how PriceStrike fits your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?type=partner">
                  <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-6 text-lg font-semibold rounded-lg">
                    Apply to Partner Program
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <p className="text-[#94A3B8] text-sm mt-6">
                Questions? Email us at <a href="mailto:hello@axiosera.com" className="text-[#2563EB] hover:underline">hello@axiosera.com</a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PartnersPage;
